// admin/src/features/exercises/filters.ts
//
// Pure filter state for the exercises list: predicates, URL-query
// (de)serialization, and faceted counts. No React here so it stays easy
// to reason about and (if it grows) to test on its own -- the page just
// wires it to useSearchParams and the loaded rows.

export type TriState = 'any' | 'with' | 'without';

export type SortKey = 'name' | 'updated' | 'missing_media';

export type Row = {
  id: string;
  name_en: string;
  name_he: string;
  sport: string;
  muscle_group: string;
  kind: string;
  archived: boolean;
  hasPhoto: boolean;
  videoCount: number;
  imageCount: number;
  hasHowTo: boolean;
  equipmentIds: string[];
  updatedAt: string;
};

export type Filters = {
  sport: string | null;
  muscles: Set<string>;
  kind: string | null;
  equipmentIds: Set<string>;
  photo: TriState;
  video: TriState;
  images: TriState;
  howto: TriState;
  showArchived: boolean;
  sort: SortKey;
};

export const EMPTY_FILTERS: Filters = {
  sport: null,
  muscles: new Set(),
  kind: null,
  equipmentIds: new Set(),
  photo: 'any',
  video: 'any',
  images: 'any',
  howto: 'any',
  showArchived: false,
  sort: 'name',
};

export type FilterAxis = keyof Filters;

function triMatches(state: TriState, has: boolean): boolean {
  if (state === 'any') return true;
  return state === 'with' ? has : !has;
}

/** Whether `row` passes every filter in `filters` except the one(s) named in `exclude` (used to compute faceted counts). */
export function matches(row: Row, filters: Filters, exclude: ReadonlySet<FilterAxis> = new Set()): boolean {
  if (!exclude.has('showArchived') && !filters.showArchived && row.archived) return false;
  if (!exclude.has('sport') && filters.sport && row.sport !== filters.sport) return false;
  if (!exclude.has('muscles') && filters.muscles.size > 0 && !filters.muscles.has(row.muscle_group)) return false;
  if (!exclude.has('kind') && filters.kind && row.kind !== filters.kind) return false;
  if (!exclude.has('equipmentIds') && filters.equipmentIds.size > 0 && !row.equipmentIds.some((id) => filters.equipmentIds.has(id))) {
    return false;
  }
  if (!exclude.has('photo') && !triMatches(filters.photo, row.hasPhoto)) return false;
  if (!exclude.has('video') && !triMatches(filters.video, row.videoCount > 0)) return false;
  if (!exclude.has('images') && !triMatches(filters.images, row.imageCount > 0)) return false;
  if (!exclude.has('howto') && !triMatches(filters.howto, row.hasHowTo)) return false;
  return true;
}

export function sortRows(rows: Row[], sort: SortKey): Row[] {
  const copy = [...rows];
  switch (sort) {
    case 'updated':
      return copy.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    case 'missing_media':
      return copy.sort((a, b) => {
        const aMissing = (a.hasPhoto ? 0 : 1) + (a.videoCount > 0 ? 0 : 1);
        const bMissing = (b.hasPhoto ? 0 : 1) + (b.videoCount > 0 ? 0 : 1);
        if (aMissing !== bMissing) return bMissing - aMissing;
        return a.name_en.localeCompare(b.name_en);
      });
    case 'name':
    default:
      return copy.sort((a, b) => a.name_en.localeCompare(b.name_en));
  }
}

/** True when no filter narrows the result set (search text is handled separately by the page). */
export function isDefault(filters: Filters): boolean {
  return (
    filters.sport === null &&
    filters.muscles.size === 0 &&
    filters.kind === null &&
    filters.equipmentIds.size === 0 &&
    filters.photo === 'any' &&
    filters.video === 'any' &&
    filters.images === 'any' &&
    filters.howto === 'any' &&
    filters.showArchived === false &&
    filters.sort === 'name'
  );
}

// --- URL (de)serialization -------------------------------------------------

const TRI_VALUES: TriState[] = ['any', 'with', 'without'];
const SORT_VALUES: SortKey[] = ['name', 'updated', 'missing_media'];

function parseTri(v: string | null): TriState {
  return v && (TRI_VALUES as string[]).includes(v) ? (v as TriState) : 'any';
}

function parseSort(v: string | null): SortKey {
  return v && (SORT_VALUES as string[]).includes(v) ? (v as SortKey) : 'name';
}

export function filtersFromParams(params: URLSearchParams): Filters {
  const musclesCsv = params.get('muscle');
  const equipCsv = params.get('equip');
  return {
    sport: params.get('sport') || null,
    muscles: new Set(musclesCsv ? musclesCsv.split(',').filter(Boolean) : []),
    kind: params.get('kind') || null,
    equipmentIds: new Set(equipCsv ? equipCsv.split(',').filter(Boolean) : []),
    photo: parseTri(params.get('photo')),
    video: parseTri(params.get('video')),
    images: parseTri(params.get('images')),
    howto: parseTri(params.get('howto')),
    showArchived: params.get('archived') === '1',
    sort: parseSort(params.get('sort')),
  };
}

/** Writes `filters` onto `params` in place, removing keys that are back at their default (keeps the URL tidy). */
export function filtersToParams(params: URLSearchParams, filters: Filters): void {
  const set = (key: string, value: string | null) => {
    if (value) params.set(key, value);
    else params.delete(key);
  };
  set('sport', filters.sport);
  set('muscle', filters.muscles.size ? [...filters.muscles].join(',') : null);
  set('kind', filters.kind);
  set('equip', filters.equipmentIds.size ? [...filters.equipmentIds].join(',') : null);
  set('photo', filters.photo !== 'any' ? filters.photo : null);
  set('video', filters.video !== 'any' ? filters.video : null);
  set('images', filters.images !== 'any' ? filters.images : null);
  set('howto', filters.howto !== 'any' ? filters.howto : null);
  set('archived', filters.showArchived ? '1' : null);
  set('sort', filters.sort !== 'name' ? filters.sort : null);
}
